import { ReactNode } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type { Pitch } from "@/modules/zoom-calls/types";
import {
  asTrimmedString,
  formatLabel,
  isHiddenTechnicalKey,
} from "@/modules/zoom-calls/components/analysis.helpers";
import { formatAnyValue } from "./extracted-fields.helpers";
import ClientReactionRow from "./client-reaction-row";

// Rendered by <ClientReactionRow/> (icon + RTL Arabic quote), or merged into the
// "Project" row below — excluded from the generic key/value grid to avoid duplicates.
const HANDLED_ELSEWHERE = new Set([
  "client_reaction",
  "client_reaction_evidence",
  "objection",
  "project",
  "developer",
]);

const PitchCard = ({
  pitch,
  index,
  total,
}: {
  pitch: Pitch;
  index: number;
  total: number;
}) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;

  const project = asTrimmedString(pitch.project);
  const developer = asTrimmedString(pitch.developer);

  const rows: Array<{ label: string; value: ReactNode }> = [];

  // Project + developer merged into a single headline row; everything else is shown
  // generically so no field the backend sends is dropped.
  if (project) {
    rows.push({
      label: "Project",
      value: (
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {project}
          </Box>
          {developer && (
            <>
              <Box
                component="span"
                sx={{ fontWeight: 400, color: p.grey[700] }}
              >
                {" by "}
              </Box>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {developer}
              </Box>
            </>
          )}
        </>
      ),
    });
  } else if (developer) {
    rows.push({ label: "Developer", value: developer });
  }

  for (const [key, raw] of Object.entries(pitch)) {
    if (HANDLED_ELSEWHERE.has(key) || isHiddenTechnicalKey(key)) continue;
    const value = formatAnyValue(raw);
    if (value) rows.push({ label: formatLabel(key), value });
  }

  const hasReaction = !!(
    pitch.client_reaction ||
    pitch.client_reaction_evidence ||
    (pitch.objection && (pitch.objection.type || pitch.objection.quote))
  );

  if (rows.length === 0 && !hasReaction) return null;

  // Per Figma: the "Pitch #N" label sits on the grey requirement card; the detail grid
  // and the client-reaction row each get their own white rounded box.
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: "16px",
          color: theme.nawy.colors.semantic.text.tertiary,
          whiteSpace: "nowrap",
        }}
      >
        {total > 1 ? `Pitch #${index + 1}` : "Pitch"}
      </Typography>

      {rows.length > 0 && (
        <Box
          sx={{
            borderRadius: "10px",
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
            p: "12px",
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: "16px",
            rowGap: "6px",
          }}
        >
          {rows.map((row) => (
            <Box key={row.label} sx={{ display: "contents" }}>
              <Typography
                sx={{ fontSize: 12, color: p.grey[700], whiteSpace: "nowrap" }}
              >
                {row.label}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: p.navy[800],
                  wordBreak: "break-word",
                }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <ClientReactionRow pitch={pitch} />
    </Box>
  );
};

export default PitchCard;
